import {
  LitElement,
  css,
  html,
  classMap,
  styleMap,
  ref,
  createRef,
} from './-global.min.js'

class HHDetailsModal extends LitElement {
  static properties = {
    breakpoint: {
      type: String,
    },
    label: {
      type: String,
    },
    selector: {
      type: String,
    },
    open: {
      type: Boolean,
      attribute: 'is-open',
      reflect: true,
    },
    forcedOpen: {
      attribute: 'open',
    },
    lockHeight: {
      type: Boolean,
      attribute: 'lock-height',
    },
    heightOffset: {
      type: Number,
      attribute: 'heigh-offset',
    },
    _height: {
      state: true,
    },
  }

  $container = createRef()
  $details = createRef()

  constructor() {
    super()

    this.open = false
    this.lockHeight = false
    this.heightOffset = 3
    this.breakpoint = '(min-width: 768px)'
    this.bodyListener = this.bodyListener.bind(this)
    this.disableBodyScroll = this.disableBodyScroll.bind(this)
    this.enableBodyScroll = this.enableBodyScroll.bind(this)
  }

  requestUpdate(name, value) {
    // Allows parents to close open modals
    if (name === 'forcedOpen') {
      this.toggle(false, !this.forcedOpen)
    }
    return super.requestUpdate(name, value)
  }

  interceptEvent(e) {
    e._intercepted = true
  }

  toggle(e = false, value = undefined) {
    // Prevent multiple toggles for the same event
    if (e && e.type === 'keyup' && e.code.toUpperCase() !== 'ENTER') {
        return true;
    }

    if (e) {
        e.preventDefault();
    }

    // Determine the new `open` state
    const newOpenState = typeof value === 'undefined' ? !this.open : value;

    // Only proceed if the state is actually changing
    if (newOpenState === this.open) {
        return;
    }

    // Update the `open` state
    this.open = newOpenState;

    // Find the button in the DOM
    const modalHost = document.querySelector('#desktop-sticky-menu');
    const button = modalHost ? modalHost.querySelector('button[slot="summary"]') : null;

    if (button) {
        // Set `aria-expanded` attribute based on `this.open`
        button.setAttribute('aria-expanded', this.open ? 'true' : 'false');
        console.log(`aria-expanded set to ${this.open ? 'true' : 'false'}`);
    } else {
        console.error('Button not found in the current context.');
    }

    // Perform additional actions if the modal is opening
    if (this.open) {
      this.disableBodyScroll();
      this.setHeight();
      this.setModalHeight();
    }

    if (this.open) {
        this.broadcast();
    }
}


  disableBodyScroll() {
    document.querySelector('html').style.overflow = 'hidden'
    document.querySelector('body').style.overflow = 'hidden'
  }

  enableBodyScroll() {
    document.querySelector('html').style.overflow = ''
    document.querySelector('body').style.overflow = ''
  }

  closeFromDetails(e) {
    if (
      e.path.some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      this.toggle()
    }
  }

  closeWithEscape(e) {
    if (e.code.toUpperCase() === 'ESCAPE') {
      this.toggle()
    }
  }

  isMobile() {
    return !window.matchMedia(this.breakpoint).matches
  }

  addBodyListener() {
    this.isMobile() && this.disableBodyScroll()
    this.isMobile() === false && document.body.addEventListener('click', (e) => this.bodyListener(e))
  }

  removeBodyListener() {
    this.enableBodyScroll()
    this.isMobile() === false && document.body.removeEventListener('click', (e) => this.bodyListener(e))
  }

  bodyListener(e) {
    !e._intercepted && this.toggle()
  }

  setHeight() {
    const { top: parentTop = 0 } = this.parentNode.getBoundingClientRect() || {}
    const { top: detailsTop = 0 } =
      this.$details?.value.getBoundingClientRect() || {}
    let offset = parentTop + detailsTop - this.heightOffset
    if (this.classList.value.includes('mobile-menu')) {
      offset = detailsTop - this.heightOffset
    }
    this._height = this.isMobile()
      ? `calc(100vh - ${Math.floor(offset)}px)`
      : ''
  }

  setModalHeight() {
    const stickyHeaderHeight = document.querySelector('hh-sticky-header').scrollHeight;
    const modalContent = document.querySelector('.header__submenu--child');
    const paddingBottom = '40px';
    if (modalContent && !this.isMobile()) {
      modalContent.style.height = `calc(100vh - ${Math.floor(stickyHeaderHeight)}px - ${paddingBottom})`;
      modalContent.style.overflow = 'scroll';
    }
  }

  broadcast() {
    this.dispatchEvent(
      new CustomEvent('modalOpened', {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
        },
      })
    )
  }

  render() {
    return html` <!-- @ts-ignore no-incompatible-type-binding -->
      <details
        ?open="${this.open}"
        @click="${this.interceptEvent}"
        ${ref(this.$container)}
      >
        <summary
          @keyup="${this.toggle}"
          aria-expanded="${this.open}"
          aria-haspopup="dialog"
          ${ref(this.$container)}
        >
          <slot
            @click="${this.toggle}"
            class="${classMap({ 'is-open': this.open })}"
            name="summary"
            tabindex="-1"
          ></slot>
        </summary>
        <div
          class="content"
          role="dialog"
          aria-modal="true"
          aria-label="${this.label}"
          style="${styleMap({ height: this._height })}"
          ${ref(this.$details)}
        >
          <hh-focus-trap active="${this.open}">
            <slot
              name="details"
              @keyup="${this.closeWithEscape}"
              @click="${this.closeFromDetails}"
            ></slot>
          </hh-focus-trap>
        </div>
      </details>`
  }

  static styles = css`
    summary {
      list-style-type: none;
    }

    summary:focus {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    summary::-webkit-details-marker {
      display: none;
    }

    .content {
      position: var(--hh-position, absolute);
      top: var(--hh-top);
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--hh-modal-background);
      height: var(--hh-modal-height);
      z-index: var(--hh-z-index-inner, 1);
    }

    ::slotted([slot='details']) {
      width: 100%;
      display: flex;
    }

    :host {
      z-index: var(--hh-z-index, 100);
    }
  `
}

customElements.define('hh-details-modal', HHDetailsModal)
